import { FC, useState } from 'react';
import {
  Button,
  Grid,
  Typography,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Card,
  CardContent,
  Box,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import { useForm } from '@tanstack/react-form';
import { observer } from 'mobx-react';
import { AxiosError } from 'axios';
import BaseConfig from './BaseConfig';
import useCompanyId from '../../../hooks/useCompanyId';
import { usePrices, useResellerPrices } from '../../../stores/FetcherStores/PricesFetcher';
import { BillingItemType, SubscriptionBillingItem } from '../../../model/BillingItem';
import { useCreateBillingItemMutation } from '../../../stores/FetcherStores/BillingItemFetcher';
import { Money } from '../../../utils/currency';
import { useGetCompany } from '../../../stores/FetcherStores/CompanyFetcher';

interface EnklaOrderConfigProps {
  onClose: () => void;
}

const EnklaOrderConfig: FC<EnklaOrderConfigProps> = observer(({ onClose }) => {
  const companyId = useCompanyId();
  const { data: company } = useGetCompany(companyId);
  const {
    mutateAsync: createBillingItem,
    isLoading: isCreating,
    error,
  } = useCreateBillingItemMutation();
  const [pricingOption, setPricingOption] = useState<'monthly' | 'transaction'>('monthly');
  const { data: prices } = usePrices();
  const { data: resellerPrices } = useResellerPrices(company?.resellerId || '');

  const form = useForm({
    defaultValues: {
      startDate: DateTime.now(),
      endDate: null as DateTime | null,
      prepaidPeriod: 1,
    },
    onSubmit: async ({ value }) => {
      const billingItem: Omit<
        SubscriptionBillingItem,
        'id' | 'createdAt' | 'createdBy' | 'discount' | 'taxRate'
      > = {
        type: BillingItemType.SUBSCRIPTION,
        subscriptionId: 'ENKLA_ORDER',
        startDate: value.startDate.toISO(),
        endDate: value.endDate?.toISO() || undefined,
        unitPrice:
          resellerPrices?.subscriptions.ENKLA_ORDER.price ??
          prices?.subscriptions.ENKLA_ORDER.price ??
          new Money(0),
        isDemo: false,
        quantity: 1,
      };
      await createBillingItem(companyId, billingItem)
        .then(() => {
          onClose();
        })
        .catch(() => {});
    },
  });

  return (
    <BaseConfig
      title="Enklaorder"
      description="Med Enklaorder kan du låta kunden betala via webben, från dela notan vid bordet, till bordsbeställning och onlinebeställning."
      price={
        pricingOption === 'monthly'
          ? `${prices?.subscriptions.ENKLA_ORDER.price.format()}/mån`
          : '0kr/mån'
      }
      resellerPrice={
        pricingOption === 'monthly'
          ? `${resellerPrices?.subscriptions.ENKLA_ORDER.price.format()}/mån`
          : '0kr/mån'
      }
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      errorMessage={
        error instanceof AxiosError ? error?.response?.data?.name || error?.message : error?.message
      }
      buttons={
        <>
          <Button variant="outlined" onClick={onClose} color="secondary">
            Avbryt
          </Button>
          {pricingOption === 'monthly' ? (
            <Button
              type="submit"
              variant="contained"
              disabled={form.state.isSubmitting || isCreating}
              color="secondary"
            >
              {form.state.isSubmitting || isCreating ? 'Aktiverar...' : 'Aktivera Enkla Order'}
            </Button>
          ) : (
            <Button variant="contained" disabled>
              Redan aktiv
            </Button>
          )}
        </>
      }
    >
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2 }}>
          Välj prissättning
        </Typography>
        <FormControl component="fieldset" fullWidth>
          <RadioGroup
            value={pricingOption}
            onChange={(e) => setPricingOption(e.target.value as 'monthly' | 'transaction')}
          >
            <Card variant="outlined" sx={{ mb: 2, p: 2 }}>
              <CardContent sx={{ p: '0 !important' }}>
                <FormControlLabel
                  value="monthly"
                  control={<Radio color="secondary" />}
                  label={
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Månadsavgift
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        499kr/mån + 1.49% transaktionsavgift
                      </Typography>
                      <Typography variant="body2" color="secondary">
                        Profit share: {Math.round(0.49 * 0.2 * 100) / 100}%
                      </Typography>
                    </Box>
                  }
                />
              </CardContent>
            </Card>
            <Card variant="outlined" sx={{ p: 2 }}>
              <CardContent sx={{ p: '0 !important' }}>
                <FormControlLabel
                  value="transaction"
                  control={<Radio color="secondary" />}
                  label={
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Endast transaktionsavgift
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        0kr/mån + 5% transaktionsavgift (Kräver Viva.com)
                      </Typography>
                      <Typography variant="body2" color="secondary">
                        Profit share: {Math.round(4 * 0.2 * 100) / 100}%
                      </Typography>
                    </Box>
                  }
                />
              </CardContent>
            </Card>
          </RadioGroup>
        </FormControl>
      </Grid>
      {pricingOption === 'monthly' && (
        <>
          <form.Field name="startDate">
            {(field) => (
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Startdatum"
                  value={field.state.value}
                  minDate={DateTime.now()}
                  onChange={(date) => field.handleChange(date ?? DateTime.now())}
                  format="yyyy-MM-dd"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText: field.state.meta.errors.join(', ') || 'Välj startdatum',
                      error: field.state.meta.errors.length > 0,
                    },
                  }}
                />
              </Grid>
            )}
          </form.Field>

          <form.Field name="endDate">
            {(field) => (
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Slutdatum (valfritt)"
                  value={field.state.value}
                  minDate={DateTime.now().plus({ days: 1 })}
                  onChange={(date) => field.handleChange(date)}
                  openTo="month"
                  views={['year', 'month', 'day']}
                  format="yyyy-MM-dd"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText: field.state.meta.errors.join(', ') || 'Lämna tomt för löpande',
                      error: field.state.meta.errors.length > 0,
                    },
                    field: { clearable: true, onClear: () => field.handleChange(null) },
                  }}
                />
              </Grid>
            )}
          </form.Field>
        </>
      )}
    </BaseConfig>
  );
});

export default EnklaOrderConfig;
